import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { INTAKE_COLUMNS, INTAKE_SHELL } from './layout';
import { SaveIndicator, type SaveStatus } from './SaveIndicator';

/**
 * The sticky header of both forms (crossmarc/steward-app#1886, from the IntakeHeader it
 * replaces): the firm's wordmark in the rail's track, the save status, an optional cluster at
 * the right (the onboarding form's client name and Sign out), a phone-only "Section N of M"
 * button that opens `sheet`, and a 2px bronze progress hairline under the band.
 *
 * ## Why the band is `bg-cream` and not a dark slab
 *
 * `navy` is the ink ROLE and flips in dark mode (#0F172A light, #EDEAE2 dark), so a `bg-navy`
 * slab rendered as a bright bar across a dark page. `bg-ink` holds one dark but has no
 * constant-light partner, so every child would hard-code `text-white`. The header takes the
 * page's own role and recedes in both themes.
 *
 * The band is a `relative` wrapper carrying the background, not the <header>: a negative-z
 * scrim paints above its stacking context's own background, so a background on <header> would
 * grey the band while its text sat over it untouched.
 *
 * The progress hairline marks POSITION, not completion.
 */
export function FormHeader({
  mark, suffix, save, onRetry, right, positionLabel, position, total, sheet, sheetFooter, sheetKey,
}: {
  /** The firm's logo or wordmark. */
  mark: ReactNode;
  /** Set on the onboarding form: "with Steward". */
  suffix?: string;
  save: SaveStatus;
  onRetry: () => void;
  /** Shown from `lg` up; below it the same content goes in `sheetFooter`. */
  right?: ReactNode;
  /** The phone button's text, e.g. "Section 3 of 8" or "Review". */
  positionLabel: string;
  /** 1-based place among all rail rows, for the hairline. */
  position: number;
  total: number;
  /** The rail, rendered inside the sheet. Receives a `close` to call on a pick. */
  sheet: (close: () => void) => ReactNode;
  sheetFooter?: ReactNode;
  /** Changes when the current section changes, so a move made elsewhere closes the sheet. */
  sheetKey: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const panelId = useId();

  // A move made from somewhere else closes the sheet too. Picking the row you are already on
  // does not change the key, so `close` (passed to `sheet`) handles that case.
  useEffect(() => setOpen(false), [sheetKey]);

  // Focus follows the sheet in, onto the current row. A disclosure, not a trap.
  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLElement>('[aria-current="step"]')?.focus();
  }, [open]);

  // Escape closes and hands focus back to the button that opened it.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <header className="sticky top-0 z-20">
      <div className="relative bg-cream shadow-[0_1px_12px_-6px_rgba(15,23,42,0.3)]">
        <div className={`${INTAKE_SHELL} ${INTAKE_COLUMNS} flex h-14 items-center justify-between gap-3`}>
          <div className="flex min-w-0 flex-1 items-baseline gap-2 lg:flex-none lg:overflow-visible lg:whitespace-nowrap">
            {mark}
            {suffix && <span className="hidden shrink-0 font-sans text-[12px] text-slate sm:inline">{suffix}</span>}
          </div>
          <div className="flex shrink-0 items-center justify-end gap-4">
            <SaveIndicator status={save} onRetry={onRetry} />
            {right && <div className="hidden items-center gap-4 lg:flex">{right}</div>}
            <button
              ref={triggerRef}
              type="button"
              onClick={() => setOpen((wasOpen) => !wasOpen)}
              aria-expanded={open}
              aria-controls={open ? panelId : undefined}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-token border border-line-strong bg-transparent px-3 py-1.5 max-sm:min-h-11 font-sans text-[13px] text-navy tabular-nums lg:hidden"
            >
              {positionLabel}
              <ChevronDown aria-hidden="true" className={`h-4 w-4 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        <div
          className="h-0.5 w-full bg-navy/10"
          role="progressbar"
          aria-label="Progress through the form"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={position}
        >
          <div className="h-full bg-bronze transition-[width] duration-200" style={{ width: `${(position / total) * 100}%` }} />
        </div>
      </div>

      {open && (
        <>
          {/* `ink` is the one token that stays dark in both themes, which is a scrim's job. */}
          <div aria-hidden="true" className="fixed inset-0 -z-10 bg-ink/40 lg:hidden" onClick={() => setOpen(false)} />
          <nav
            ref={panelRef}
            id={panelId}
            aria-label="Jump to a section"
            className="absolute inset-x-0 top-full max-h-[calc(100dvh-3.5rem)] overflow-y-auto border-b border-line bg-surface px-4 py-2 shadow-[0_12px_24px_-12px_rgba(15,23,42,0.35)] sm:px-6 lg:hidden"
          >
            {sheet(() => { setOpen(false); triggerRef.current?.focus(); })}
            {sheetFooter && <div className="mt-2 flex items-center justify-between gap-4 border-t border-line py-3">{sheetFooter}</div>}
          </nav>
        </>
      )}
    </header>
  );
}
